import { supabase, isSupabaseConfigured, getCurrentSession } from "@/lib/supabase";
import { aiService } from "@/services/aiService";
import { operatingLoopService } from "@/services/operatingLoopService";
import { creditsService } from "@/services/creditsService";
import { chatThreadsService } from "@/services/chatThreadsService";
import {
  parseSuggestions,
  suggestionDedupKey,
  buildExtractionPrompt,
  type SmartTask,
  type TaskPriority,
  type TaskStatus,
  type SuggestedTaskItem,
} from "@/lib/smartTasksPure";

/**
 * Smart Tasks — canonical task domain service (DS EDIT 2 jul26).
 * Tables: smart_tasks / smart_task_sources / smart_task_activity /
 * smart_task_suggestions (migration 069). RLS is authoritative; this service
 * adds defensive filters only. AI extraction goes ONLY through
 * aiService.streamChat → ai-proxy (sanitized, rate-limited, metered — Phase 3).
 * Article 9: task queries are NOT in the RQ persistence allowlist (no
 * IndexedDB caching of task bodies); previews are capped; no titles/bodies in
 * logs or analytics.
 */

export interface TaskSource {
  id: string;
  taskId: string;
  sourceType: "thread" | "message" | "document" | "discussion" | "admin_workflow";
  sourceId: string;
  preview: string | null;
  createdAt: string;
}

export interface TaskActivity {
  id: string;
  taskId: string;
  actorUserId: string | null;
  action: string;
  meta: Record<string, unknown>;
  createdAt: string;
}

export interface TaskSuggestion {
  id: string;
  workspaceId: string | null;
  contextType: "thread" | "document" | "recent_activity";
  contextId: string | null;
  proposed: SuggestedTaskItem;
  reason: string | null;
  state: "pending" | "approved" | "dismissed" | "expired";
  createdAt: string;
  expiresAt: string;
}

const T = "smart_tasks";
const SRC = "smart_task_sources";
const ACT = "smart_task_activity";
const SUG = "smart_task_suggestions";

/* eslint-disable @typescript-eslint/no-explicit-any */
function mapTask(r: any): SmartTask {
  return {
    id: r.id,
    ownerUserId: r.owner_user_id,
    workspaceId: r.workspace_id ?? null,
    assigneeUserId: r.assignee_user_id ?? null,
    agentId: r.agent_id ?? null,
    title: r.title,
    description: r.description ?? null,
    status: r.status as TaskStatus,
    priority: r.priority as TaskPriority,
    dueAt: r.due_at ?? null,
    completedAt: r.completed_at ?? null,
    origin: r.origin,
    sensitivity: r.sensitivity ?? "standard",
    discussionThreadId: r.discussion_thread_id ?? null,
    createdAt: r.created_at,
    updatedAt: r.updated_at,
  };
}

class SmartTasksService {
  private async uid(): Promise<string> {
    const session = await getCurrentSession();
    const id = session?.user?.id;
    if (!id) throw new Error("Not signed in");
    return id;
  }

  private async logActivity(taskId: string, action: string, meta: Record<string, unknown> = {}): Promise<void> {
    try {
      const userId = await this.uid();
      await supabase.from(ACT).insert({ task_id: taskId, actor_user_id: userId, action, meta });
    } catch {
      // activity is best-effort; never blocks the primary operation
    }
  }

  // ── task CRUD ──────────────────────────────────────────────────────────────

  async listTasks(): Promise<SmartTask[]> {
    if (!isSupabaseConfigured) return [];
    const { data, error } = await supabase
      .from(T)
      .select("*")
      .neq("status", "archived")
      .order("updated_at", { ascending: false })
      .limit(500);
    if (error) throw new Error(error.message);
    return (data ?? []).map(mapTask);
  }

  async listArchived(): Promise<SmartTask[]> {
    const { data, error } = await supabase
      .from(T).select("*").eq("status", "archived")
      .order("updated_at", { ascending: false }).limit(200);
    if (error) throw new Error(error.message);
    return (data ?? []).map(mapTask);
  }

  async createTask(input: {
    title: string;
    description?: string;
    workspaceId?: string | null;
    assigneeUserId?: string | null;
    priority?: TaskPriority;
    dueAt?: string | null;
    origin?: SmartTask["origin"];
    suggestionId?: string;
    sensitivity?: "standard" | "sensitive";
    source?: { sourceType: TaskSource["sourceType"]; sourceId: string; preview?: string };
  }): Promise<SmartTask> {
    const userId = await this.uid();
    const { data, error } = await supabase
      .from(T)
      .insert({
        owner_user_id: userId,
        created_by: userId,
        title: input.title.trim().slice(0, 300),
        description: input.description?.slice(0, 4000) ?? null,
        workspace_id: input.workspaceId ?? null,
        assignee_user_id: input.assigneeUserId ?? null,
        priority: input.priority ?? "none",
        due_at: input.dueAt ?? null,
        origin: input.origin ?? "manual",
        suggestion_id: input.suggestionId ?? null,
        sensitivity: input.sensitivity ?? "standard",
      })
      .select("*")
      .single();
    if (error || !data) throw new Error(error?.message ?? "Create failed");
    const task = mapTask(data);
    void this.logActivity(task.id, "created", { origin: task.origin });
    if (input.source) {
      await this.linkSource(task.id, input.source.sourceType, input.source.sourceId, input.source.preview);
    }
    return task;
  }

  async updateTask(id: string, patch: Partial<{
    title: string; description: string | null; priority: TaskPriority;
    dueAt: string | null; assigneeUserId: string | null; agentId: string | null; status: TaskStatus;
  }>): Promise<SmartTask> {
    const row: Record<string, unknown> = {};
    if (patch.title !== undefined) row.title = patch.title.trim().slice(0, 300);
    if (patch.description !== undefined) row.description = patch.description;
    if (patch.priority !== undefined) row.priority = patch.priority;
    if (patch.dueAt !== undefined) row.due_at = patch.dueAt;
    if (patch.assigneeUserId !== undefined) row.assignee_user_id = patch.assigneeUserId;
    if (patch.agentId !== undefined) row.agent_id = patch.agentId;
    if (patch.status !== undefined) row.status = patch.status;
    const { data, error } = await supabase.from(T).update(row).eq("id", id).select("*").single();
    if (error || !data) throw new Error(error?.message ?? "Update failed");
    const action =
      patch.status === "done" ? "completed"
      : patch.status === "open" ? "reopened"
      : patch.status === "archived" ? "archived"
      : patch.assigneeUserId !== undefined ? "assignment_changed"
      : "updated";
    void this.logActivity(id, action, { fields: Object.keys(row) });
    return mapTask(data);
  }

  async deleteTask(id: string): Promise<void> {
    const { error } = await supabase.from(T).delete().eq("id", id);
    if (error) throw new Error(error.message);
  }

  // ── sources + activity ─────────────────────────────────────────────────────

  async linkSource(taskId: string, sourceType: TaskSource["sourceType"], sourceId: string, preview?: string): Promise<void> {
    const userId = await this.uid();
    const { error } = await supabase.from(SRC).insert({
      task_id: taskId,
      source_type: sourceType,
      source_id: sourceId,
      preview: preview?.slice(0, 280) ?? null,
      created_by: userId,
    });
    if (error) throw new Error(error.message);
    void this.logActivity(taskId, "source_linked", { sourceType });
  }

  async getSources(taskId: string): Promise<TaskSource[]> {
    const { data, error } = await supabase.from(SRC).select("*").eq("task_id", taskId).order("created_at");
    if (error) throw new Error(error.message);
    return (data ?? []).map((r: any) => ({
      id: r.id, taskId: r.task_id, sourceType: r.source_type,
      sourceId: r.source_id, preview: r.preview ?? null, createdAt: r.created_at,
    }));
  }

  async getActivity(taskId: string): Promise<TaskActivity[]> {
    const { data, error } = await supabase.from(ACT).select("*").eq("task_id", taskId)
      .order("created_at", { ascending: false }).limit(100);
    if (error) throw new Error(error.message);
    return (data ?? []).map((r: any) => ({
      id: r.id, taskId: r.task_id, actorUserId: r.actor_user_id ?? null,
      action: r.action, meta: r.meta ?? {}, createdAt: r.created_at,
    }));
  }

  // ── discussion (canonical chat-threads; no duplicate messaging system) ─────

  async openDiscussion(task: SmartTask): Promise<string> {
    if (task.discussionThreadId) return task.discussionThreadId;
    const thread = await chatThreadsService.createChatThread(
      {
        title: `Task: ${task.title}`.slice(0, 200),
        messages: [{
          id: `task-seed-${Date.now()}`,
          role: "user",
          content: `Discussion for the task "${task.title}".${task.description ? `\n\n${task.description}` : ""}`,
          timestamp: new Date().toISOString(),
        }] as never,
      } as never,
      task.workspaceId,
    );
    const threadId = String((thread as { id: string | number }).id);
    await supabase.from(T).update({ discussion_thread_id: threadId }).eq("id", task.id);
    void this.logActivity(task.id, "discussion_opened", {});
    return threadId;
  }

  // ── AI: explicit extraction + suggestions (approval required) ──────────────

  /** Explicit extraction from context the user selected AND can already read. */
  async extractFromContext(input: {
    contextType: "thread" | "document" | "recent_activity";
    contextId: string | null;
    contextLabel: string;
    contextText: string;             // caller supplies text it fetched under RLS
    workspaceId?: string | null;
  }): Promise<TaskSuggestion[]> {
    const gate = await creditsService.assertPositiveBalanceForAi();
    if (!gate.ok) throw new Error("Not enough AI credits.");
    const userId = await this.uid();

    const prompt = buildExtractionPrompt(input.contextLabel, input.contextText);
    const gated = await operatingLoopService.answerWithLoop({
      userId,
      question: prompt,
      pickedModel: "openai/gpt-4o-mini",
      autoSelectBest: true,
      selectedDocumentIds: input.contextType === "document" && input.contextId ? [input.contextId] : [],
      app: "smart_tasks",
      run: async ({ model, briefPrompt }) => {
        let raw = "";
        for await (const chunk of aiService.streamChat({
          model,
          messages: [
            ...(briefPrompt ? [{ role: "system" as const, content: briefPrompt }] : []),
            { role: "user" as const, content: prompt },
          ],
          stream: true,
          maxTokens: 1200,
          temperature: 0.2,
          app: "smart_tasks",
        })) {
          if (chunk.type === "error") throw new Error(chunk.error || "Extraction failed");
          if (chunk.type === "delta" && chunk.content) raw += chunk.content;
        }
        return { text: raw };
      },
    });
    const raw = gated.text;
    const items = parseSuggestions(raw);
    if (!items.length) return [];

    // persist as PENDING suggestions (never auto-tasks); dedup vs dismissed
    const rows = items.map((p) => ({
      user_id: userId,
      workspace_id: input.workspaceId ?? null,
      context_type: input.contextType,
      context_id: input.contextId,
      proposed: p,
      reason: p.reason ?? null,
      state: "pending",
      dedup_key: suggestionDedupKey(input.contextType, input.contextId, p.title),
    }));
    const { data, error } = await supabase
      .from(SUG)
      .upsert(rows, { onConflict: "user_id,dedup_key", ignoreDuplicates: true })
      .select("*");
    if (error) throw new Error(error.message);
    return (data ?? []).map(this.mapSuggestion);
  }

  private mapSuggestion(r: any): TaskSuggestion {
    return {
      id: r.id, workspaceId: r.workspace_id ?? null, contextType: r.context_type,
      contextId: r.context_id ?? null, proposed: r.proposed, reason: r.reason ?? null,
      state: r.state, createdAt: r.created_at, expiresAt: r.expires_at,
    };
  }

  async listPendingSuggestions(): Promise<TaskSuggestion[]> {
    // lazy expiry: mark overdue pendings expired, then list
    await supabase.from(SUG).update({ state: "expired" })
      .eq("state", "pending").lt("expires_at", new Date().toISOString());
    const { data, error } = await supabase.from(SUG).select("*")
      .eq("state", "pending").order("created_at", { ascending: false }).limit(50);
    if (error) throw new Error(error.message);
    return (data ?? []).map(this.mapSuggestion);
  }

  /** Approve → creates the task (explicit user action) and marks the suggestion. */
  async approveSuggestion(s: TaskSuggestion, edits?: Partial<SuggestedTaskItem>): Promise<SmartTask> {
    const p = { ...s.proposed, ...edits };
    const task = await this.createTask({
      title: p.title,
      description: p.description,
      priority: p.priority ?? "none",
      dueAt: p.due_at ?? null,
      workspaceId: s.workspaceId,
      origin: s.contextType === "recent_activity" ? "proactive_ai_suggestion" : "explicit_ai_extraction",
      suggestionId: s.id,
      source: s.contextId
        ? { sourceType: s.contextType === "document" ? "document" : "thread", sourceId: s.contextId, preview: s.reason ?? undefined }
        : undefined,
    });
    await supabase.from(SUG).update({ state: "approved" }).eq("id", s.id);
    return task;
  }

  async dismissSuggestion(id: string): Promise<void> {
    const { error } = await supabase.from(SUG).update({ state: "dismissed" }).eq("id", id);
    if (error) throw new Error(error.message);
  }

  // ── export / delete (Art. 15 / 17 for the task domain) ─────────────────────

  async exportMyTasks(): Promise<Record<string, unknown>> {
    const [tasks, archived, suggestions] = await Promise.all([
      this.listTasks(), this.listArchived(),
      supabase.from(SUG).select("*").then((r: { data: unknown[] | null }) => r.data ?? []),
    ]);
    return { exportedAt: new Date().toISOString(), tasks: [...tasks, ...archived], suggestions };
  }

  async deleteAllMyTaskData(): Promise<void> {
    const userId = await this.uid();
    // suggestions + owned tasks (cascades sources/activity); RLS restricts scope
    await supabase.from(SUG).delete().eq("user_id", userId);
    const { error } = await supabase.from(T).delete().eq("owner_user_id", userId);
    if (error) throw new Error(error.message);
  }
}

export const smartTasksService = new SmartTasksService();

export const tasksQueryKey = (userId: string | undefined | null) => ["smart-tasks", userId ?? "anon"];
export const suggestionsQueryKey = (userId: string | undefined | null) => ["smart-task-suggestions", userId ?? "anon"];
export const archivedTasksQueryKey = (userId: string | undefined | null) => ["smart-tasks-archived", userId ?? "anon"];
